import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import useAdminDialog from '../components/AdminDialog';
import { useAdminLanguage } from '../AdminLanguageContext';
import { retryRequest } from '../utils/retryRequest';

const yuan = cents => (Number(cents || 0) / 100).toFixed(2);

const badgeClass = {
  active: 'badge-green',
  pending: 'badge-yellow',
  suspended: 'badge-red',
  verified: 'badge-green',
  unverified: 'badge-gray',
};

export default function Sellers() {
  const { copy } = useAdminLanguage();
  const sellersCopy = copy.sellers;
  const { confirmAction, dialog } = useAdminDialog();
  const [data, setData] = useState({ sellers: [], total: 0 });
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSellers = useCallback(() => {
    setLoading(true);
    setError('');
    retryRequest(() => api.get('/admin/sellers', { params: { keyword, status, page, limit: 20 } }))
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || sellersCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [keyword, page, sellersCopy.errors.loadFailed, status]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const handleVerification = async seller => {
    const nextStatus = seller.verification_status === 'verified' ? 'unverified' : 'verified';
    const action = nextStatus === 'verified'
      ? sellersCopy.actions.verify
      : sellersCopy.actions.reject;
    if (!await confirmAction({
      title: sellersCopy.confirm.verification.title,
      message: sellersCopy.confirm.verification.message(action, seller.username),
      variant: nextStatus === 'verified' ? 'primary' : 'danger',
    })) return;
    try {
      await retryRequest(() => api.put(`/admin/sellers/${seller.id}/verification`, { status: nextStatus }));
      fetchSellers();
    } catch (err) {
      setError(err.response?.data?.error || sellersCopy.errors.loadFailed);
    }
  };

  const handleSuspension = async seller => {
    const suspended = seller.account_status !== 'suspended';
    const action = suspended ? sellersCopy.actions.suspend : sellersCopy.actions.unsuspend;
    if (!await confirmAction({
      title: sellersCopy.confirm.suspension.title,
      message: sellersCopy.confirm.suspension.message(action, seller.username),
      variant: suspended ? 'danger' : 'primary',
    })) return;
    await api.put(`/admin/sellers/${seller.id}/suspension`, { suspended });
    fetchSellers();
  };

  const statusLabel = seller => sellersCopy.status[seller.account_status] || seller.account_status;
  const verificationLabel = seller => sellersCopy.verification[seller.verification_status] || sellersCopy.verification.unverified;

  return (
    <div>
      {dialog}
      <div className="page-title">{sellersCopy.title}</div>
      <div className="card">
        <div className="seller-toolbar">
          <div className="search-input">
            <input
              value={keyword}
              placeholder={sellersCopy.searchPlaceholder}
              onChange={event => {
                setKeyword(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <select
            value={status}
            aria-label={sellersCopy.statusFilter}
            onChange={event => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="all">{sellersCopy.status.all}</option>
            <option value="active">{sellersCopy.status.active}</option>
            <option value="pending">{sellersCopy.status.pending}</option>
            <option value="suspended">{sellersCopy.status.suspended}</option>
          </select>
          <span className="seller-total">{sellersCopy.total(data.total)}</span>
        </div>

        {error && (
          <div className="seller-error">
            <span>{error}</span>
            <button className="btn btn-sm btn-outline" onClick={fetchSellers} disabled={loading}>{sellersCopy.actions.retry}</button>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{sellersCopy.columns.seller}</th>
                <th>{sellersCopy.columns.rating}</th>
                <th>{sellersCopy.columns.orders}</th>
                <th>{sellersCopy.columns.settlement}</th>
                <th>{sellersCopy.columns.services}</th>
                <th>{sellersCopy.columns.verification}</th>
                <th>{sellersCopy.columns.account}</th>
                <th>{sellersCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="seller-empty">{sellersCopy.loading}</td></tr>
              ) : data.sellers.length === 0 ? (
                <tr><td colSpan={8} className="seller-empty">{sellersCopy.empty}</td></tr>
              ) : data.sellers.map(seller => (
                <tr key={seller.id}>
                  <td>
                    <div className="seller-name">{seller.username}</div>
                    <div className="seller-email">{seller.email}</div>
                  </td>
                  <td>★ {Number(seller.avg_rating || 0).toFixed(1)}</td>
                  <td>{seller.completed_orders}</td>
                  <td className="seller-money">¥ {yuan(seller.seller_settlement)}</td>
                  <td>{seller.service_count}</td>
                  <td>
                    <span className={`badge ${badgeClass[seller.verification_status] || 'badge-gray'}`}>
                      {verificationLabel(seller)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${badgeClass[seller.account_status] || 'badge-gray'}`}>
                      {statusLabel(seller)}
                    </span>
                  </td>
                  <td>
                    <div className="seller-actions">
                      <button className="btn btn-sm btn-outline" onClick={() => handleVerification(seller)}>
                        {seller.verification_status === 'verified'
                          ? sellersCopy.actions.reject
                          : sellersCopy.actions.verify}
                      </button>
                      <button
                        className={`btn btn-sm ${seller.account_status === 'suspended' ? 'btn-outline' : 'btn-warning'}`}
                        onClick={() => handleSuspension(seller)}
                      >
                        {seller.account_status === 'suspended'
                          ? sellersCopy.actions.unsuspend
                          : sellersCopy.actions.suspend}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <button className="btn btn-sm btn-outline" disabled={page === 1} onClick={() => setPage(current => current - 1)}>
            {sellersCopy.previous}
          </button>
          <span>{sellersCopy.page(page)}</span>
          <button className="btn btn-sm btn-outline" disabled={page * 20 >= data.total} onClick={() => setPage(current => current + 1)}>
            {sellersCopy.next}
          </button>
        </div>
      </div>
      <style>{`
        .seller-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .seller-toolbar select { min-width: 140px; padding: 9px 12px; }
        .seller-total { margin-left: auto; color: #66726f; font-size: 13px; white-space: nowrap; }
        .seller-empty { text-align: center; color: #9ca3af; padding: 36px; }
        .seller-error { margin-bottom: 12px; color: #b42318; font-size: 13px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .seller-name { font-weight: 700; color: #18211f; }
        .seller-email { color: #66726f; font-size: 12px; margin-top: 3px; }
        .seller-money { color: #0f7f5c; font-weight: 700; white-space: nowrap; }
        .seller-actions { display: flex; gap: 6px; min-width: 170px; }
        @media (max-width: 900px) {
          .seller-toolbar { align-items: stretch; flex-wrap: wrap; }
          .seller-toolbar .search-input { flex: 1 1 220px; }
          .seller-total { width: 100%; margin-left: 0; }
          .seller-actions { flex-direction: column; min-width: 90px; }
        }
      `}</style>
    </div>
  );
}
